import { useMemo } from 'react'
import { useContentQuery } from './useContentQuery'
import { formatCurrency } from '@/apps/cms/utils/misc'
import { useAppCtx } from '../providers/useAppCtx'

type TranslationNamespace =
  | 'navigation'
  | 'voting'
  | 'cart'
  | 'checkout'
  | 'thank_you'
  | 'legal'
  | 'recap_video'
  | 'contestant_content'

const MUSTACHE_TAG = /\{\{\s*([A-Za-z0-9_-]+)\s*\}\}/g

type MustacheIndex = Record<string, string>

const buildMustacheIndex = (dict: Record<string, any>): MustacheIndex => {
  const index: Record<string, string> = {}
  const stack: unknown[] = [dict]

  while (stack.length) {
    const current = stack.pop()

    if (!current || typeof current !== 'object') continue

    if (Array.isArray(current)) {
      for (let i = 0; i < current.length; i++) {
        const value = current[i]
        if (value && typeof value === 'object') stack.push(value)
      }
      continue
    }

    const obj = current as Record<string, unknown>
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase()

      if (value != null && typeof value === 'object') {
        stack.push(value)
      } else if (
        value !== undefined &&
        typeof value !== 'function' &&
        !(lowerKey in index)
      ) {
        index[lowerKey] = String(value)
      }
    }
  }

  return index
}

const getReplacement = (
  index: MustacheIndex,
  rawName: string,
): string | undefined => {
  const key = rawName.trim()
  const lower = key.toLowerCase()
  const upper = key.toUpperCase()

  const value =
    (index as any)[key] ?? (index as any)[lower] ?? (index as any)[upper]

  if (value === undefined || value === null) return undefined
  if (typeof value === 'function') return undefined

  return String(value)
}

export const replaceMustachesInString = (
  input: string = '',
  index: MustacheIndex,
): string => {
  if (!input) {
    return ''
  }

  let hasMatch = false

  const result = input.replace(
    MUSTACHE_TAG,
    (match: string, rawName: string): string => {
      hasMatch = true
      const replacement = getReplacement(index, rawName)
      return replacement !== undefined ? replacement : match
    },
  )

  return hasMatch ? result : input
}

const interpolateDictionary = (
  dict: Record<string, any>,
  index: MustacheIndex,
): Record<string, any> => {
  const seen = new WeakMap<object, unknown>()

  const transform = (value: unknown): unknown => {
    if (value == null) return value

    const valueType = typeof value

    if (valueType === 'string') {
      return replaceMustachesInString(value as string, index)
    }

    if (valueType !== 'object') return value

    if (Array.isArray(value)) {
      const arr = value as unknown[]
      if (arr.length === 0) return value

      let changed = false
      const next: unknown[] = new Array(arr.length)

      for (let i = 0; i < arr.length; i++) {
        const originalItem = arr[i]
        const transformedItem = transform(originalItem)
        if (transformedItem !== originalItem) changed = true
        next[i] = transformedItem
      }

      return changed ? next : value
    }

    const obj = value as Record<string, unknown>

    const cached = seen.get(obj)
    if (cached) return cached

    let result: Record<string, unknown> | null = null

    for (const [key, val] of Object.entries(obj)) {
      const transformedVal = transform(val)
      if (transformedVal !== val) {
        if (!result) {
          result = { ...obj }
          seen.set(obj, result)
        }
        result[key] = transformedVal
      }
    }

    return result || value
  }

  return transform(dict) as Record<string, any>
}

export const useMustacheIndexStatic: () => MustacheIndex = () => {
  const { data: winner } = useContentQuery('winner')
  const {
    activeLanguage,
    votesCount,
    minVotes = 1,
    maxVotes,
    price,
    currency,
  } = useAppCtx()

  return {
    PRICE_PER_VOTE: String(price),
    CURRENCY: currency,
    VOTES_COUNT: String(votesCount),
    MAX_VOTES: String(maxVotes),
    SINGLE_VOTE_PRICE_LABEL: formatCurrency(
      price / 100 || 99,
      currency || 'eur',
      activeLanguage || 'en',
    ),
    REMAINING_VOTES: String(minVotes - votesCount),
    TOTAL_PRICE_LABEL: formatCurrency(
      (price * votesCount) / 100 || 99,
      currency || 'eur',
      activeLanguage || 'en',
    ),
    WINNER: winner,
  }
}
export const useMustacheIndex = (): MustacheIndex => {
  const { data: dictionary = {}, checksum } = useContentQuery('dictionary')
  const staticContent = useMustacheIndexStatic()

  return useMemo(() => {
    const baseIndex: MustacheIndex = buildMustacheIndex(dictionary)

    for (const [key, value] of Object.entries(staticContent)) {
      if (value === undefined || value === null) continue
      if (typeof value === 'function') continue
      baseIndex[key] = value
      baseIndex[key.toLowerCase()] = value
    }

    const interpolated = interpolateDictionary(dictionary, baseIndex)
    const index: MustacheIndex = buildMustacheIndex(interpolated)

    for (const [key, value] of Object.entries(staticContent)) {
      if (value === undefined || value === null) continue
      if (typeof value === 'function') continue
      index[key] = value
      index[key.toLowerCase()] = value
    }

    return index
  }, [checksum, staticContent])
}

export const useTranslations = (namespace: TranslationNamespace) => {
  const {
    data: dictionary = {},
    loading,
    error,
    checksum: checksumDictionary,
  } = useContentQuery('dictionary')

  const mustacheIndex = useMustacheIndex()

  const processedDictionary = useMemo(() => {
    if (!dictionary || typeof dictionary !== 'object') return dictionary
    if (!Object.keys(mustacheIndex).length) return dictionary

    return interpolateDictionary(dictionary, mustacheIndex)
  }, [dictionary, checksumDictionary, mustacheIndex])

  return {
    data: processedDictionary?.[namespace],
    loading,
    error,
    checksum: checksumDictionary,
  }
}
